import { useEffect, useRef, useState } from 'react';

const integrations = [
  { name: 'HubSpot', color: '#FF7A59', bg: 'rgba(255,122,89,0.12)', icon: 'ri-bar-chart-box-line' },
  { name: 'Salesforce', color: '#00A1E0', bg: 'rgba(0,161,224,0.10)', icon: 'ri-cloud-line' },
  { name: 'Zapier', color: '#FF4A00', bg: 'rgba(255,74,0,0.10)', icon: 'ri-flashlight-line' },
  { name: 'Make', color: '#A259FF', bg: 'rgba(162,89,255,0.12)', icon: 'ri-loop-right-line' },
  { name: 'Slack', color: '#E01E5A', bg: 'rgba(224,30,90,0.10)', icon: 'ri-slack-line' },
  { name: 'Google', color: '#34A853', bg: 'rgba(52,168,83,0.10)', icon: 'ri-google-line' },
  { name: 'Airtable', color: '#18BFFF', bg: 'rgba(24,191,255,0.10)', icon: 'ri-table-line' },
  { name: 'Monday', color: '#FF3D57', bg: 'rgba(255,61,87,0.10)', icon: 'ri-layout-grid-line' },
  { name: 'GoHighLevel', color: '#00C389', bg: 'rgba(0,195,137,0.10)', icon: 'ri-rocket-line' },
  { name: 'ActiveCampaign', color: '#356AE6', bg: 'rgba(53,106,230,0.10)', icon: 'ri-mail-send-line' },
];

const integrationsRow2 = [
  { name: 'Calendly', color: '#006BFF', bg: 'rgba(0,107,255,0.10)', icon: 'ri-calendar-check-line' },
  { name: 'Twilio', color: '#F22F46', bg: 'rgba(242,47,70,0.10)', icon: 'ri-message-3-line' },
  { name: 'Notion', color: '#6B5B9C', bg: 'rgba(107,91,156,0.10)', icon: 'ri-file-text-line' },
  { name: 'ClickUp', color: '#7B68EE', bg: 'rgba(123,104,238,0.10)', icon: 'ri-checkbox-multiple-line' },
  { name: 'Zoom', color: '#2D8CFF', bg: 'rgba(45,140,255,0.10)', icon: 'ri-vidicon-line' },
  { name: 'Stripe', color: '#635BFF', bg: 'rgba(99,91,255,0.10)', icon: 'ri-bank-card-line' },
  { name: 'Mailchimp', color: '#E3A316', bg: 'rgba(227,163,22,0.10)', icon: 'ri-mail-line' },
  { name: 'Pipedrive', color: '#1A9940', bg: 'rgba(26,153,64,0.10)', icon: 'ri-filter-2-line' },
  { name: 'Asana', color: '#F06A6A', bg: 'rgba(240,106,106,0.10)', icon: 'ri-task-line' },
  { name: 'Teams', color: '#6264A7', bg: 'rgba(98,100,167,0.10)', icon: 'ri-team-line' },
];

interface IntegrationBadgeProps {
  item: typeof integrations[0];
}

const IntegrationBadge = ({ item }: IntegrationBadgeProps) => (
  <div
    className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-white/10 flex-shrink-0 select-none"
    style={{ background: item.bg }}
  >
    <div
      className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
      style={{ background: `${item.color}25` }}
    >
      <i className={`${item.icon} text-sm`} style={{ color: item.color }} />
    </div>
    <span className="font-inter text-sm font-medium text-white/80 whitespace-nowrap">{item.name}</span>
  </div>
);

const IntegrationsSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const doubled1 = [...integrations, ...integrations];
  const doubled2 = [...integrationsRow2, ...integrationsRow2];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="integrations"
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #0f0e2e 0%, #1E1B4B 55%, #1a1740 100%)' }}
    >
      {/* Top accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-violet/40 to-transparent" />
      {/* Glow orbs */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-80 h-80 rounded-full bg-brand-violet/8 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-64 h-64 rounded-full bg-brand-teal/6 blur-3xl pointer-events-none" />

      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className="text-center max-w-2xl mx-auto mb-14"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="font-inter text-xs font-semibold text-brand-teal tracking-widest uppercase mb-4 block">
            Integrations
          </span>
          <h2
            className="font-space font-semibold text-white leading-tight mb-5"
            style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)' }}
          >
            We Build On The{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #7C3AED, #14B8A6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Tools You Already Use
            </span>
          </h2>
          <p className="font-inter text-white/55 text-base leading-relaxed">
            We connect and automate across 100+ platforms — your CRM, scheduling tools, communication apps, and more — without making you switch software.
          </p>
        </div>
      </div>

      {/* Marquee rows — full width, no max-width constraint */}
      <div
        style={{
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.8s ease 0.3s',
        }}
      >
        {/* Row 1 — scroll left */}
        <div className="relative overflow-hidden mb-4" style={{ maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)' }}>
          <div className="flex gap-3 animate-marquee-left">
            {doubled1.map((item, i) => (
              <IntegrationBadge key={`r1-${i}`} item={item} />
            ))}
          </div>
        </div>

        {/* Row 2 — scroll right */}
        <div className="relative overflow-hidden" style={{ maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)' }}>
          <div className="flex gap-3 animate-marquee-right">
            {doubled2.map((item, i) => (
              <IntegrationBadge key={`r2-${i}`} item={item} />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom stats row */}
      <div
        className="max-w-7xl mx-auto px-6 lg:px-8 mt-14"
        style={{
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.7s ease 0.6s',
        }}
      >
        <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
          {[
            { value: '100+', label: 'Platforms Supported' },
            { value: 'No-code', label: 'Setup — We Handle It All' },
            { value: '48h', label: 'Average Onboarding Time' },
          ].map((stat, i) => (
            <div
              key={i}
              className="text-center px-4 py-5 rounded-xl border border-white/8 bg-white/4"
            >
              <p
                className="font-space font-semibold text-white mb-1"
                style={{ fontSize: '1.5rem' }}
              >
                {stat.value}
              </p>
              <p className="font-inter text-white/45 text-xs leading-snug">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom accent */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-teal/30 to-transparent" />
    </section>
  );
};

export default IntegrationsSection;
